<template>
    <TVJoyDialog class="pre-accept-dialog">
        <template #dialog-header>{{ $t('projectclient.quote.view.quote-summary.dialog.pre-accept.title') }}</template>
        <template #dialog-subheader>{{ $t('projectclient.quote.view.quote-summary.dialog.pre-accept.subtitle') }}</template>

        <template #dialog-body>
            <p class="pre-accept-dialog__members-title">{{ $t('projectclient.quote.view.quote-summary.dialog.pre-accept.radiogroup.title') }}</p>
            <div v-for="(clientAdmin, key) in companyAdmins" :key="key">
                <VJoyCheckbox
                    :id="clientAdmin.accountId"
                    name="companyAdmins"
                    :data-testid="`pre-accept-company-admin-${key}`"
                    :checked="selectedCompanyMemberIds.includes(clientAdmin.accountId)"
                    @update:checked="updateSelection($event, clientAdmin.accountId)"
                >
                    <VJoyWrapper no-margin column-gap="3">
                        <VJoyAvatar size="small" :photo-url="clientAdmin.photoUrl" :full-name="`${clientAdmin.firstName} ${clientAdmin.lastName}`" />
                        {{ clientAdmin.firstName }} {{ clientAdmin.lastName }}
                    </VJoyWrapper>
                </VJoyCheckbox>
            </div>
        </template>

        <template #dialog-confirm>
            <VJoyButton variant="ghost" @click="closeDialog">
                {{ $t('general.cancel') }}
            </VJoyButton>

            <VJoyButton data-testid="pre-accept-confirm" :disabled="!selectedCompanyMemberIds.length" variant="main" @click="preAcceptQuote">
                {{ $t('general.confirm') }}
            </VJoyButton>
        </template>
    </TVJoyDialog>
</template>

<script setup lang="ts">
    import {ref, onMounted} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyButton, VJoyAvatar, VJoyWrapper, VJoyCheckbox, pushVJoySnackbar} from '@maltjoy/core-vue';
    import {useDialogRoot} from '~/common/composables';
    import {useCompanyInfoStore, useClientQuoteViewStore} from '../../../stores';

    const {TVJoyDialog, closeDialog, confirmDialog} = useDialogRoot();
    const {t} = useTranslation();

    const props = defineProps<{
        quoteId: string;
        clientCompanyId: string;
    }>();

    const companyInfoStore = useCompanyInfoStore();
    onMounted(async () => await companyInfoStore.getCompanyAdmins(props.clientCompanyId));

    const {companyAdmins} = storeToRefs(companyInfoStore);

    const clientQuoteStore = useClientQuoteViewStore();

    const selectedCompanyMemberIds = ref<string[]>([]);

    function updateSelection($event: boolean, memberId: string) {
        if ($event) {
            selectedCompanyMemberIds.value.push(memberId);
        } else {
            selectedCompanyMemberIds.value = selectedCompanyMemberIds.value.filter((m) => m !== memberId);
        }
    }

    async function preAcceptQuote() {
        if (selectedCompanyMemberIds.value.length) {
            await clientQuoteStore.preAcceptQuote(props.quoteId, selectedCompanyMemberIds.value);
            await clientQuoteStore.getQuote(props.quoteId);

            pushVJoySnackbar({
                props: {
                    level: 'success',
                    header: t('projectclient.quote.view.quote-summary.dialog.pre-accept.success.notification.header'),
                    message: t('projectclient.quote.view.quote-summary.dialog.pre-accept.success.notification.message'),
                },
            });
        }
        confirmDialog();
    }
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .pre-accept-dialog {
        &__members-title {
            @include text.getFontBySize(primary-450);
        }
    }

    :deep(.joy-checkbox__input-wrapper) {
        align-self: center;
    }
</style>
